/**
 * Known-crawler detection for edge bucketing.
 *
 * Its own module (not `./index`) so the CloudFront viewer-request bundle pays
 * for the pattern and nothing else, and so the SSR-lambda fallback and the
 * future API call site test the SAME pattern — a second copy of this regex is
 * a second bot policy, which is exactly the drift the parity spine exists to
 * prevent (see §3.8 of
 * `docs/projects/active/edge-experiments-account-pinned-bucketing.md`).
 *
 * Crawlers are pinned to each experiment's `fallbackVariant` rather than
 * bucketed, so a search engine indexes exactly one arm per URL and all bot
 * traffic collapses onto a single `x-variants` cache entry per path.
 *
 * Delimited tokens, not a UA allow-list: `bot` followed by `/`, `)`, `-`
 * or end-of-string covers Googlebot/2.1, bingbot, Applebot,
 * DuckDuckBot-Https, YandexBot, SemrushBot and the LLM crawlers, at a
 * fraction of the bytes a name list costs against the ~3.6 KB edge budget.
 * The delimiters are load-bearing: real phone UAs contain `bot` as a bare
 * substring (`CUBOT NOTE 20`, `CUBOT_NOTE_20`), and a false positive at the
 * edge is PERMANENT — no `lis_bucket` cookie is ever minted, so that human
 * is served the fallback arm on every visit and never enters a denominator.
 * Space, underscore and semicolon are therefore deliberately NOT
 * delimiters: space/underscore appear inside model names, and `;` is
 * Android's separator between platform tokens, so a brand-only entry like
 * `(Linux; Android 11; CUBOT; wv)` would read as a bot. Dropping `;` costs
 * no known crawler — even `PetalBot;` UAs end in `…/petalbot)`. The real
 * misses are token-less bots (`facebookexternalhit`,
 * `Google-InspectionTool`), and those degrade gracefully — an unrecognized
 * crawler indexes a hash-assigned arm, which §3.5 mitigates by choosing the
 * fallback as the safe arm (for price tests, the top-priced one). The
 * asymmetry decides every token here: a missed bot is noise, a mislabeled
 * human is stranded.
 *
 * The pattern must stay self-sufficient: `handler-request.ts` calls `.test()`
 * on it directly rather than going through `isBotUserAgent` (routing through
 * the helper measured 36 bytes the edge budget does not have), so
 * normalization added inside the helper would apply on one side of the parity
 * only. Put any such change in the pattern itself.
 */
export const BOT_USER_AGENT_PATTERN =
  /bot[/)-]|bot$|crawl|spider[/);+]|spider$|slurp/i;

/** `true` for a user-agent that names a known crawler. */
export function isBotUserAgent(userAgent: string | undefined): boolean {
  return userAgent !== undefined && BOT_USER_AGENT_PATTERN.test(userAgent);
}
